import type { Movable2D } from "./motion-2d";

export type FacingBody = Movable2D & {
  facing: number;
  facingHoldUntil: number;
};

export type FacingKnobs = {
  facingFlipEps: number;
  facingHoldSec: number;
};

export type ArriveKnobs = {
  arriveStop: number;
  arriveExitMul: number;
};

export function applyFacingFromIntent(
  e: FacingBody,
  intentVx: number,
  time: number,
  knobs: FacingKnobs,
): void {
  const eps = knobs.facingFlipEps;
  if (Math.abs(intentVx) < eps) return;
  if (time < e.facingHoldUntil) return;
  const next = intentVx >= 0 ? 1 : -1;
  if (next === e.facing) return;
  e.facing = next;
  e.facingHoldUntil = time + knobs.facingHoldSec;
}

function arriveExit(knobs: ArriveKnobs): number {
  return knobs.arriveStop * (knobs.arriveExitMul || 1.5);
}

export function setDesiredToward(
  e: FacingBody,
  tx: number,
  ty: number,
  speedMul: number,
  time: number,
  arrive: ArriveKnobs,
  facing: FacingKnobs,
): void {
  const d = Math.hypot(tx - e.x, ty - e.y);
  const stop = arrive.arriveStop;
  const exit = arriveExit(arrive);

  if (e.arrived) {
    if (d <= exit) {
      e.vx = 0;
      e.vy = 0;
      e.intentVx = 0;
      e.intentVy = 0;
      return;
    }
    e.arrived = false;
  } else if (d < stop) {
    e.arrived = true;
    e.vx = 0;
    e.vy = 0;
    e.intentVx = 0;
    e.intentVy = 0;
    return;
  }

  const sp = e.speed * speedMul;
  e.vx = ((tx - e.x) / d) * sp;
  e.vy = ((ty - e.y) / d) * sp;
  e.intentVx = e.vx;
  e.intentVy = e.vy;
  applyFacingFromIntent(e, e.intentVx, time, facing);
}

export type RangeBandKnobs = {
  /** Must come inside maxDist - enterPad to count as "in band" (hold). */
  enterPad: number;
  /** Must leave maxDist + exitPad before approach resumes (hysteresis). */
  exitPad: number;
};

export type BandVerdict = "approach" | "hold" | "retreat";

/**
 * Settle into a distance band [minDist, maxDist] around a target, with
 * hysteresis so units don't oscillate on the band edge. Unifies "arrived"
 * with "in weapon range": callers gate firing on the "hold" verdict, which
 * makes 'parked outside range doing nothing' unrepresentable.
 * Keeps the sticky-arrive semantics of setDesiredToward (v/intent zeroed on
 * hold; intent = preferred velocity for the separation/RVO layer otherwise).
 */
export function setDesiredInBand(
  e: FacingBody,
  tx: number,
  ty: number,
  minDist: number,
  maxDist: number,
  speedMul: number,
  time: number,
  band: RangeBandKnobs,
  facing: FacingKnobs,
): BandVerdict {
  const dx = tx - e.x;
  const dy = ty - e.y;
  const d = Math.hypot(dx, dy) || 0.001;

  const approachUntil = e.arrived
    ? maxDist + band.exitPad // already settled: only re-approach past exit
    : maxDist - band.enterPad; // approaching: push inside the enter line

  let verdict: BandVerdict;
  if (d < minDist) verdict = "retreat";
  else if (d > approachUntil) verdict = "approach";
  else verdict = "hold";

  if (verdict === "hold") {
    e.arrived = true;
    e.vx = 0;
    e.vy = 0;
    e.intentVx = 0;
    e.intentVy = 0;
    // Face the target while holding (aim read)
    applyFacingFromIntent(e, dx, time, facing);
    return verdict;
  }

  e.arrived = false;
  const sp = e.speed * speedMul;
  const sign = verdict === "approach" ? 1 : -1;
  e.vx = (dx / d) * sp * sign;
  e.vy = (dy / d) * sp * sign;
  e.intentVx = e.vx;
  e.intentVy = e.vy;
  applyFacingFromIntent(e, e.intentVx, time, facing);
  return verdict;
}

/**
 * Retreat direction away from a threat, biased toward an anchor (ally
 * center-of-mass etc. — anchor computation is the caller's domain).
 * biasWeight 0 = pure away-from-threat, 1 = strongly toward the anchor.
 * Writes a normalized direction into `out` (scratch buffer convention).
 */
export function retreatDirBiased(
  fromX: number,
  fromY: number,
  threatX: number,
  threatY: number,
  anchorX: number,
  anchorY: number,
  biasWeight: number,
  out: { x: number; y: number },
): void {
  let ax = fromX - threatX;
  let ay = fromY - threatY;
  const al = Math.hypot(ax, ay) || 0.001;
  ax /= al;
  ay /= al;
  let bx = anchorX - fromX;
  let by = anchorY - fromY;
  const bl = Math.hypot(bx, by);
  if (bl > 0.001) {
    bx /= bl;
    by /= bl;
  } else {
    bx = 0;
    by = 0;
  }
  let x = ax + biasWeight * bx;
  let y = ay + biasWeight * by;
  const l = Math.hypot(x, y);
  if (l < 0.001) {
    // Anchor directly opposes the away vector: slide perpendicular.
    x = -ay;
    y = ax;
  } else {
    x /= l;
    y /= l;
  }
  out.x = x;
  out.y = y;
}
